import _ from 'lodash';
import type { AttributeDefinition } from '@/shared/helpers/api/types/entities.ts';

type CodeEditValue = string | number | boolean | null | undefined;
type GenericViewCode = Record<string, CodeEditValue> & {
    Id?: number;
    Identity: string;
    Name?: string;
    Description?: string | null;
    IsValid?: boolean;
    AttachmentKey?: string | null;
};
type LegacyCodeValues = Record<string, CodeEditValue> & {
    identity: string;
    id?: number;
    name?: string;
    description?: string | null;
    isValid?: boolean;
    attachmentKey?: string | null;
};
type LegacyCodeObject = {
    values: LegacyCodeValues;
    meta: Record<string, CodeEditValue>;
};

type CodeDefWithoutValueIdx = Omit<CodeDef, 'valueIdx'>;

export interface CodeDef {
    field: string;
    label: string | null;
    required: boolean;
    includeInIdentity: boolean;
    type: string;
    valueIdx: number;
    visible?: boolean;
    attribute?: boolean;
    description?: string | null;
    sequenceNumber?: number;
    refLibraryName?: string | null;
    referenceDisplayMode?: string | number;
    referenceFilterStatic?: string;
    referenceFilterStaticErrorMessage?: string;
    regex?: string | null;
    regexErrorMessage?: string | null;
    defaultValue?: string | null;
}

export interface CreateCodeDefArrayOptions {
    nameInIdentity?: boolean;
    filterFunc?: (def: CodeDefWithoutValueIdx) => boolean;
    descriptionIsRequired?: boolean;
    descriptionRegex?: string | null;
}

export function capitalize(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export function createCodeDefArray(attributesDefs: AttributeDefinition[], options: CreateCodeDefArrayOptions = {}): CodeDef[] {
    const nameInIdentity = options.nameInIdentity ?? true;
    const filterFunc = options.filterFunc ?? (() => true);
    const descriptionIsRequired = options.descriptionIsRequired ?? false;
    const descriptionRegex = options.descriptionRegex ?? null;

    const constDefs: CodeDefWithoutValueIdx[] = [
        { field: 'identity', label: 'Identity', required: true, includeInIdentity: true, type: 'STRING' },
        { field: 'name', label: 'Name', required: true, includeInIdentity: nameInIdentity, type: 'STRING' },
        { field: 'isValid', label: 'Is valid', required: true, includeInIdentity: false, type: 'BOOL' },
        { field: 'description', label: 'Description', required: descriptionIsRequired, includeInIdentity: false, type: 'STRING', regex: descriptionRegex },
        { field: 'dateCreated', label: 'Date created', required: true, includeInIdentity: false, type: 'DATETIME' },
        { field: 'dateUpdated', label: 'Date modified', required: true, includeInIdentity: false, type: 'DATETIME' },
        { field: 'attachmentKey', label: 'AttachmentKey', required: false, includeInIdentity: false, type: 'STRING' },
        { field: 'commonLibraryIRI', label: 'CommonLibraryIRI', required: false, includeInIdentity: false, type: 'STRING' }
    ];

    const defs = constDefs.concat(attributesDefs
        .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
        .map((ad): CodeDefWithoutValueIdx => ({
            attribute: true,
            field: ad.name,
            description: ad.description,
            label: ad.displayAs,
            sequenceNumber: ad.sequenceNumber,
            required: ad.required,
            includeInIdentity: ad.includeInIdentity,
            type: ad.attributeType,
            refLibraryName: ad.referenceLibraryName,
            referenceDisplayMode: ad.referenceDisplayMode,
            referenceFilterStatic: ad.referenceFilterStatic,
            referenceFilterStaticErrorMessage: ad.referenceFilterStaticErrorMessage,
            regex: ad.regex,
            regexErrorMessage: ad.regexErrorMessage,
            defaultValue: ad.defaultValue
        })))
        .filter(filterFunc);

    return defs.map((def, valueIdx) => ({ ...def, valueIdx }));
}

export function createCodeObject4(code: GenericViewCode, codeFields: CodeDef[]): LegacyCodeObject {
    const codeObj: LegacyCodeObject = {
        values: {
            id: code.Id,
            identity: code.Identity,
            name: code.Name,
            description: code.Description,
            isValid: code.IsValid,
            attachmentKey: code.AttachmentKey
        },
        meta: {
            dateCreated: '',
            dateUpdated: ''
        }
    };

    codeFields.forEach(field => {
        if (field.attribute) {
            const hasField = code[field.field] !== null && code[field.field] !== undefined;
            codeObj.values[field.field] = hasField ? code[field.field] : '';
        }
    });

    return codeObj;
}

export function transformToChangeDocCode(newCode: Record<string, CodeEditValue>, originalCode: Record<string, CodeEditValue> | null = null) {
    const code: Record<string, CodeEditValue> = {};
    for (const prop in newCode) {
        if (['identity', 'referenceCodes'].includes(prop))
            continue;
        if (['name', 'description', 'isValid'].includes(prop))
            code[_.upperFirst(prop)] = newCode[prop];
        else if (originalCode && newCode[prop] === '' && originalCode[prop] !== '')
            code[prop] = '{null}';
        else if (originalCode && newCode[prop] === '' && originalCode[prop] === '')
            continue;
        else if (newCode[prop] === '')
            continue;
        else
            code[prop] = newCode[prop];
    }
    return code;
}
